import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Users, Storefront, ShoppingBag, ChartLineUp, UploadSimple, EnvelopeSimple } from "@phosphor-icons/react";
import WaitlistPanel from "../components/admin/WaitlistPanel";

import AnalyticsOverview from "../components/admin/AnalyticsOverview";
import VendorManagement from "../components/admin/VendorManagement";
import OrderManagement from "../components/admin/OrderManagement";
import RefundModal from "../components/admin/RefundModal";
import BulkUpload from "../components/admin/BulkUpload";
import SupportInbox from "../components/admin/SupportInbox";

const TABS = [
  { k: "overview", label: "Overview", Icon: ChartLineUp },
  { k: "vendors", label: "Vendors", Icon: Storefront },
  { k: "orders", label: "Orders", Icon: ShoppingBag },
  { k: "bulk", label: "Bulk upload", Icon: UploadSimple },
  { k: "support", label: "Support", Icon: Users },
  { k: "waitlist", label: "Waitlist", Icon: EnvelopeSimple },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [pendingVendors, setPendingVendors] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [refundPayload, setRefundPayload] = useState(null);

  const load = useCallback(async () => {
    try {
      const [o, pv, v, ro, t] = await Promise.all([
        api.get("/admin/analytics/overview"),
        api.get("/admin/vendors/pending"),
        api.get("/vendors?status=approved"),
        api.get("/admin/orders/recent"),
        api.get("/support/tickets"),
      ]);
      setOverview(o.data);
      setPendingVendors(pv.data);
      setAllVendors(v.data);
      setRecentOrders(ro.data);
      setTickets(t.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[AdminDashboard] load failed:", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (vendor_id) => {
    try {
      await api.post(`/vendors/${vendor_id}/approve`);
      toast.success("Vendor approved");
      load();
    } catch {
      toast.error("Approval failed");
    }
  };

  const suspend = async (vendor_id) => {
    if (!window.confirm("Suspend this vendor?")) return;
    try {
      await api.post(`/vendors/${vendor_id}/suspend`);
      toast.success("Vendor suspended");
      load();
    } catch {
      toast.error("Suspension failed");
    }
  };

  const openRefund = async (order_id) => {
    try {
      const { data } = await api.get(`/admin/orders/${order_id}/full`);
      setRefundPayload(data);
    } catch {
      toast.error("Could not load order");
    }
  };

  const openTickets = tickets.filter((t) => t.status !== "resolved").length;
  const delayedOrders = recentOrders.filter((o) => {
    if (!["pending", "accepted", "preparing"].includes(o.status)) return false;
    const age = Date.now() - new Date(o.created_at).getTime();
    return age > 2 * 24 * 3600 * 1000;
  }).length;

  return (
    <div className="pt-24 pb-24 max-w-7xl mx-auto px-4 md:px-8 lg:px-12" data-testid="admin-dashboard">
      <h1 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mb-3">Admin Panel</h1>

      {(openTickets > 0 || delayedOrders > 0 || overview?.vendors.pending > 0) && (
        <div className="mb-6 grid sm:grid-cols-3 gap-3" data-testid="admin-alerts">
          {overview?.vendors.pending > 0 && (
            <button onClick={() => setTab("vendors")} className="card-surface p-4 text-left hover:border-[#A94438] transition-all" data-testid="alert-pending-vendors">
              <p className="text-[10px] font-bold tracking-wider uppercase text-[#E4A951] mb-1">⚠️ Attention needed</p>
              <p className="font-heading text-xl font-black">{overview.vendors.pending} vendor{overview.vendors.pending === 1 ? "" : "s"} awaiting approval</p>
            </button>
          )}
          {delayedOrders > 0 && (
            <button onClick={() => setTab("orders")} className="card-surface p-4 text-left hover:border-[#A94438] transition-all" data-testid="alert-delayed-orders">
              <p className="text-[10px] font-bold tracking-wider uppercase text-[#A94438] mb-1">⏱ Delayed orders</p>
              <p className="font-heading text-xl font-black">{delayedOrders} open &gt; 48h</p>
            </button>
          )}
          {openTickets > 0 && (
            <button onClick={() => setTab("support")} className="card-surface p-4 text-left hover:border-[#A94438] transition-all" data-testid="alert-open-tickets">
              <p className="text-[10px] font-bold tracking-wider uppercase text-[#2B5329] mb-1">💬 Open tickets</p>
              <p className="font-heading text-xl font-black">{openTickets} support request{openTickets === 1 ? "" : "s"}</p>
            </button>
          )}
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-[#E5E2D9] mb-8 overflow-x-auto no-scrollbar">
        {TABS.map(({ k, label, Icon }) => (
          <button
            key={k}
            data-testid={`admin-tab-${k}`}
            onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${tab === k ? "border-[#A94438] text-[#A94438]" : "border-transparent text-[#5C5C54] hover:text-[#1A1A18]"}`}
          >
            <Icon size={16} weight="duotone" /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && <AnalyticsOverview overview={overview} />}
      {tab === "vendors" && (
        <VendorManagement
          pendingVendors={pendingVendors}
          allVendors={allVendors}
          onApprove={approve}
          onSuspend={suspend}
        />
      )}
      {tab === "orders" && <OrderManagement orders={recentOrders} onRefundClick={openRefund} />}
      {tab === "bulk" && <BulkUpload onCreated={load} />}
      {tab === "support" && <SupportInbox tickets={tickets} onReload={load} />}
      {tab === "waitlist" && <WaitlistPanel />}

      {refundPayload && (
        <RefundModal
          order={refundPayload}
          onClose={() => setRefundPayload(null)}
          onDone={load}
        />
      )}
    </div>
  );
}
